import type { Persona } from '../ai/personas';

export interface PostcardData {
  persona: Persona;
  letter: string;
  reply: string;
}

export interface PostcardCallbacks {
  onClose: () => void;
}

export class PostcardShare {
  private root: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private downloadBtn: HTMLAnchorElement;
  private closeBtn: HTMLButtonElement;
  private cb: PostcardCallbacks | null = null;

  constructor(host: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'layer postcard ignore-aim hidden';
    this.root.innerHTML = `
      <div class="postcard-card">
        <div class="postcard-head">
          <div class="postcard-title">明信片</div>
          <button class="postcard-close" aria-label="关闭">×</button>
        </div>
        <div class="postcard-canvas-wrap">
          <canvas class="postcard-canvas" width="720" height="1080"></canvas>
        </div>
        <div class="postcard-actions">
          <a class="postcard-download" download="whisper-sky.png">保存图片</a>
          <p class="postcard-tip">长按图片也可保存</p>
        </div>
      </div>
    `;
    host.appendChild(this.root);
    this.canvas = this.root.querySelector('.postcard-canvas') as HTMLCanvasElement;
    this.downloadBtn = this.root.querySelector('.postcard-download') as HTMLAnchorElement;
    this.closeBtn = this.root.querySelector('.postcard-close') as HTMLButtonElement;
    this.closeBtn.addEventListener('click', () => this.close());
  }

  open(data: PostcardData, cb: PostcardCallbacks): void {
    this.cb = cb;
    this.render(data);
    this.root.classList.remove('hidden');
    requestAnimationFrame(() => this.root.classList.add('show'));
  }

  close(): void {
    this.root.classList.remove('show');
    setTimeout(() => {
      this.root.classList.add('hidden');
      this.cb?.onClose();
      this.cb = null;
    }, 220);
  }

  private render(data: PostcardData): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const { width: w, height: h } = this.canvas;
    const { persona, letter, reply } = data;

    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, this.toCss(persona.color, 0.95));
    grad.addColorStop(0.6, this.toCss(persona.accent, 0.85));
    grad.addColorStop(1, '#fdfaf0');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < 80; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      const r = Math.random() * 3.4 + 0.6;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    ctx.fillStyle = '#fffaf0';
    ctx.fillRect(60, 220, w - 120, h - 380);
    ctx.strokeStyle = 'rgba(0,0,0,0.08)';
    ctx.lineWidth = 2;
    ctx.strokeRect(60, 220, w - 120, h - 380);

    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.beginPath();
    ctx.arc(120, 130, 70, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = '92px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#222';
    ctx.fillText(persona.emoji, 120, 138);

    ctx.font = 'bold 44px "PingFang SC", "Microsoft YaHei", sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#fffaf0';
    ctx.fillText(`来自 ${persona.shortName}`, 220, 100);
    ctx.font = '24px "PingFang SC", "Microsoft YaHei", sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillText(`${persona.species} · ${persona.voice}`, 220, 158);

    ctx.fillStyle = '#5a4a35';
    ctx.font = '24px "PingFang SC", "Microsoft YaHei", sans-serif';
    this.wrapText(ctx, `寄信：${letter}`, 90, 260, w - 180, 34);

    ctx.fillStyle = '#222';
    ctx.font = '30px "PingFang SC", "Microsoft YaHei", sans-serif';
    const replyTop = 360;
    this.wrapText(ctx, reply, 90, replyTop, w - 180, 44, 14);

    ctx.fillStyle = '#3b2a18';
    ctx.font = 'bold 32px serif';
    ctx.textAlign = 'right';
    ctx.fillText(`— ${persona.shortName}`, w - 110, h - 220);

    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(w / 2 - 200, h - 110, 400, 56);
    ctx.fillStyle = '#fffaf0';
    ctx.font = 'bold 26px "PingFang SC", "Microsoft YaHei", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('风语者 · AI 纸飞机邮局', w / 2, h - 78);

    this.canvas.toBlob(blob => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      this.downloadBtn.href = url;
    }, 'image/png');
  }

  private wrapText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
    maxLines = 99
  ): number {
    let line = '';
    let cursorY = y;
    let lines = 0;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '\n') {
        ctx.fillText(line, x, cursorY);
        cursorY += lineHeight;
        line = '';
        lines++;
        if (lines >= maxLines) return cursorY;
        continue;
      }
      const test = line + ch;
      if (ctx.measureText(test).width > maxWidth && line.length > 0) {
        ctx.fillText(line, x, cursorY);
        cursorY += lineHeight;
        line = ch;
        lines++;
        if (lines >= maxLines) return cursorY;
      } else {
        line = test;
      }
    }
    if (line) ctx.fillText(line, x, cursorY);
    return cursorY + lineHeight;
  }

  private toCss(hex: number, alpha = 1): string {
    const r = (hex >> 16) & 0xff;
    const g = (hex >> 8) & 0xff;
    const b = hex & 0xff;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
